import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import CloseIcon from '@mui/icons-material/Close';
import { GoAlert } from 'react-icons/go';

import { useUnitContext } from '../../context/unit';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';

const Reset = ({ units }) => {
  const axiosPrivate = useAxiosPrivate();
  const { dispatch } = useUnitContext();
  const theme = useTheme();

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {   
    try {
      setLoading(true); 
      setError(null);

      const response = await axiosPrivate.patch(`/api/units/reset`);

      dispatch({ type: 'RESET_UNIT', payload: response.data });
      setShow(false);
    } catch (error) {
      setError(error.response?.data.error);
    } finally { 
      setLoading(false); 
    }
  };

  const handleClose = () => { 
    if (loading) 
      return; 
    
    setShow(false); 
    setError(null); 
  };

  return (
    <>
      <Tooltip title="Reset All Units State">
        <IconButton
            color="secondary"
            size="small"
            onClick={() => setShow(true)}
        >
          <LockResetIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={show}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.text.primary,
            fontWeight: 'bold',
          }}
        >
          {!error && <GoAlert size={22} color={theme.palette.error.main} />}
          {error ? 'Error' : 'Warning'}
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{ marginLeft: 'auto', color: theme.palette.text.primary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary
          }}
        >
          {!error ? (
            <Typography sx={{ marginTop: '20px' }}>
              Are you sure you want to reset the state of all units?
            </Typography>
          ) : (
            <Alert severity="error">{error}</Alert>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            backgroundColor: theme.palette.background.default,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {!error && (
            <Button
              onClick={handleReset}
              variant="contained"
              color="error"
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              {loading ? ( 
                <CircularProgress size={20} thickness={4} sx={{ color: theme.palette.common.white, }} /> 
                ) : ( 
                  'Reset' 
                )}
            </Button>
          )}
          <Button
            onClick={handleClose}
            variant="outlined"
            color="secondary"
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Reset;
